import memoizeOne from 'memoize-one';
import numbro from 'numbro';
import React, { useCallback, useEffect } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useTranslation } from 'react-i18next';
import { FaHeart } from 'react-icons/fa';
import { Dispatch } from 'redux';
import styled from 'styled-components';

import { preferenceActions } from '../../actions';
import { uiActions } from '../../actions';
import { FreeUserAlertEnum } from '../../enums';
import useDispatch from '../../hooks/useDispatch';
import { usePreferences } from '../../hooks/usePreferences';
import useRegion from '../../hooks/useRegion';
import { useMembership } from '../../hooks/users';
import useSelector from '../../hooks/useSelector';
import { Filter } from '../../shared/types';
import { ReactTable } from '../../types';
import FlatSelect from '../form/FlatSelect';
import {
  STOCK_TEMP_MKTCAP_ID,
  STOCK_TEMP_TURONVER_ID,
} from '../stock/StockTemporalTable';
import { changePercentageColumnID, RETURN_TILL_NOW_ID } from './columns';
import RangeSlider from './RangeSlider';

const StockFilterContainer = styled.div`
  input {
    max-width: 100%;
    border: none;
    border-bottom: solid 1px #aaa;
    border-radius: 0px;
  }
`;

const trimStrAndCreateFilter = (originalString: string) => {
  let str = originalString.trim();
  const operators = ['>=', '>', '<=', '<', '==', '=']; // order matters!!!
  const operaterIndex = operators.findIndex(operator =>
    str.startsWith(operator)
  );

  let operator = '';
  if (operaterIndex > -1) {
    operator = operators[operaterIndex];
    str = str.replace(operator, '');
  }

  // order matters!!!
  // units with more charactors should come first!
  const units = [
    {
      char: '千億',
      value: 100_000_000_000,
    },
    {
      char: '萬億',
      value: 1_000_000_000_000,
    },
    {
      char: '千',
      value: 1000,
    },
    {
      char: 'K',
      value: 1000,
    },
    {
      char: '萬',
      value: 10_000,
    },
    {
      char: 'M',
      value: 1_000_000,
    },
    {
      char: '億',
      value: 100_000_000,
    },
    {
      char: 'B',
      value: 1_000_000_000,
    },
  ];

  const unitFound = units.find(unit => str.includes(unit.char));

  if (unitFound) {
    str = str.replace(unitFound.char, '');
    str = String(Number.parseFloat(str) * unitFound.value);
    str = operator + str;
  }

  const filter = { operator: '', operand: str };
  if (operaterIndex >= 0) {
    filter.operator = operators[operaterIndex];
    filter['operand'] = String(
      numbro(str.replace(filter.operator, '')).value()
    );
    filter['raw'] = str;
  }
  return filter;
};

const isTurnoverId = (id: string) => {
  return id === STOCK_TEMP_TURONVER_ID || id.endsWith('-ranking-turnover');
};

const isMktCapId = (id: string) => {
  return id === STOCK_TEMP_MKTCAP_ID || id.endsWith('-ranking-mktcap');
};

const useNumberFilterInput = (id: string) => {
  const { stockTempMktcapV2, stockTempTurnoverV2 } = usePreferences();
  if (isTurnoverId(id)) {
    return stockTempTurnoverV2;
  } else if (isMktCapId(id)) {
    return stockTempMktcapV2;
  } else {
    return { us: { ranking: '', lookup: '' }, hk: { ranking: '', lookup: '' } };
  }
};

const setNumberFilterValue = (
  id: string,
  value: Filter,
  dispatch: Dispatch<any>
) => {
  if (isTurnoverId(id)) {
    dispatch(preferenceActions.setStockTempTurnover(value));
  } else if (isMktCapId(id)) {
    dispatch(preferenceActions.setStockTempMktcap(value));
  }
};

const usePercentageFilterInput = (id: string) => {
  const { returnTillNowV2, changePercentageColumnV2 } = usePreferences();
  if (id === RETURN_TILL_NOW_ID) {
    return returnTillNowV2;
  } else if (id === changePercentageColumnID) {
    return changePercentageColumnV2;
  }
  {
    return { us: { ranking: '', lookup: '' }, hk: { ranking: '', lookup: '' } };
  }
};

const setPercentageFilterValue = (
  id: string,
  value: Filter,
  dispatch: Dispatch<any>
) => {
  if (id === RETURN_TILL_NOW_ID) {
    dispatch(preferenceActions.setReturnTillNow(value));
  } else if (id === changePercentageColumnID) {
    dispatch(preferenceActions.setChangePercentageColumn(value));
  }
};

const setPage = (page: string) => {
  if (page.includes('ranking')) {
    return 'ranking';
  } else if (page.includes('lookup')) {
    return 'lookup';
  } else if (page.includes('ccass')) {
    return 'portfolio';
  }
};
export const NumberFilter: React.FC<ReactTable.FilterProps> = ({ column }) => {
  const examples = ['1.2b', '100m', '100k', '5.2b'];
  const operators = ['<', '<=', '>', '>='];
  const { region } = usePreferences();
  const input = useNumberFilterInput(column.id);
  const page = setPage(window.location.pathname);
  const regionedInput = input[region][page];
  useEffect(() => {
    regionedInput && column.setFilter(trimStrAndCreateFilter(regionedInput));
  }, [column, regionedInput]);
  const updateNumberFilterValue = useCallback(() => {
    if (regionedInput !== undefined) {
      column.setFilter(trimStrAndCreateFilter(regionedInput));
    }
  }, [column, regionedInput]);
  useEffect(() => {
    updateNumberFilterValue();
  }, [updateNumberFilterValue]);

  const dispatch = useDispatch();
  return (
    <OverlayTrigger
      trigger="focus"
      placement="right"
      overlay={
        <Tooltip id="number-filter">
          We support using k, m, b, 千萬, 億 to substitute 000, 000000,
          000000000， 0000000, 00000000
          <br />
          <br />
          Filter Examples:
          {examples.map((example, i) => (
            <React.Fragment key={i}>
              <br />
              {operators[i % 4] + example}
            </React.Fragment>
          ))}
        </Tooltip>
      }
    >
      <input
        value={input[region][page]}
        onChange={e => {
          column.setFilter(trimStrAndCreateFilter(String(e.target.value)));
          setNumberFilterValue(
            column.id,
            {
              ...input,
              [region]: {
                ...input[region],
                [page]: String(e.target.value),
              },
            },
            dispatch
          );
        }}
        placeholder={`e.g.,>${examples[0]}`}
      />
    </OverlayTrigger>
  );
};

export const PriceFilter: React.FC<ReactTable.FilterProps> = ({
  column: { setFilter },
}) => {
  const dispatch = useDispatch();
  const { stockTempPriceV2, region } = usePreferences();
  const page = window.location.pathname.includes('ranking')
    ? 'ranking'
    : 'lookup';
  useEffect(() => {
    stockTempPriceV2[region][page] &&
      setFilter(trimStrAndCreateFilter(stockTempPriceV2[region][page]));
  }, [stockTempPriceV2, region, setFilter, page]);
  return (
    <OverlayTrigger
      trigger="focus"
      placement="right"
      overlay={
        <Tooltip id="price-filter">
          Filter Examples:
          <br />
          &lt;1.0 <br />
          &lt;=100 <br />
          &gt;100
          <br />
          &gt;=5.2
        </Tooltip>
      }
    >
      <input
        value={stockTempPriceV2[region][page]}
        onChange={e => {
          setFilter(trimStrAndCreateFilter(String(e.target.value)));
          const newTempPrice = stockTempPriceV2;
          dispatch(
            preferenceActions.setStockTempPrice({
              ...newTempPrice,
              [region]: {
                ...newTempPrice[region],
                [page]: String(e.target.value),
              },
            })
          );
        }}
        placeholder="e.g., >10.9"
      />
    </OverlayTrigger>
  );
};

export const PercentageChangeFilter: React.FC<ReactTable.FilterProps> = ({
  column,
}) => {
  const dispatch = useDispatch();
  const { region } = usePreferences();
  const input = usePercentageFilterInput(column.id);
  const page = window.location.pathname.includes('ranking')
    ? 'ranking'
    : 'lookup';
  const regionedInput = input[region][page];
  useEffect(() => {
    regionedInput && column.setFilter(trimStrAndCreateFilter(regionedInput));
  }, [regionedInput, column]);
  const updatePercentageFilterValue = useCallback(() => {
    if (regionedInput !== undefined) {
      column.setFilter(trimStrAndCreateFilter(regionedInput));
    }
  }, [column, regionedInput]);

  useEffect(() => {
    updatePercentageFilterValue();
  }, [updatePercentageFilterValue]);
  return (
    <OverlayTrigger
      trigger="focus"
      placement="right"
      overlay={
        <Tooltip id="percent-chg-filter">
          Filter Examples:
          <br />
          &lt;1% <br />
          &lt;=100% <br />
          &gt;100%
          <br />
          &gt;=5.2%
        </Tooltip>
      }
    >
      <input
        value={input[region][page]}
        onChange={e => {
          column.setFilter(trimStrAndCreateFilter(String(e.target.value)));
          setPercentageFilterValue(
            column.id,
            {
              ...input,
              [region]: {
                ...input[region],
                [page]: String(e.target.value),
              },
            },
            dispatch
          );
        }}
        placeholder="e.g., >10.9%"
      />
    </OverlayTrigger>
  );
};

export const StringFilter: React.FC<ReactTable.FilterProps> = ({
  column: { setFilter },
}) => (
  <input onChange={event => setFilter(event.target.value)} placeholder="" />
);

export const StockFilter: React.FC<ReactTable.FilterProps> = ({
  column: { filterValue, setFilter },
}) => {
  const [t] = useTranslation();
  const region = useRegion();
  const placeholder = { hk: '700', us: 'AAPL' }[region];
  const validOptions = ['❤️', ''];

  const keyword = (filterValue || '').startsWith('K#')
    ? filterValue.slice(2)
    : '';

  return (
    <StockFilterContainer>
      <FlatSelect<string>
        options={validOptions}
        labels={[<FaHeart key={0} />, t('All')]}
        onValueChange={setFilter}
        value={validOptions.includes(filterValue) ? filterValue : ''}
      />
      <OverlayTrigger
        trigger="focus"
        placement="right"
        overlay={
          <Tooltip id="price-filter">
            Filter Examples:
            <br />
            AAPL <br />
            700 <br />
            00700
          </Tooltip>
        }
      >
        <input
          value={keyword}
          onChange={e =>
            e.target.value ? setFilter('K#' + e.target.value) : setFilter('')
          }
          placeholder={placeholder}
        />
      </OverlayTrigger>
    </StockFilterContainer>
  );
};

const getRangeFilterValue = memoizeOne((left: number, right: number) => [
  left,
  right,
]);

const onSliderChange = (left, right, setFilter) => {
  if (setFilter) {
    setFilter(getRangeFilterValue(left, right));
  }
};

export const ConcentrationFilterWithoutSlider: React.FC = () => {
  const dispatch = useDispatch();
  const { topN } = usePreferences();

  return (
    <FlatSelect
      prefix="Top"
      options={[3, 5, 10, 15]}
      labels={[3, 5, 10, 15].map(n => `${n}`)}
      onValueChange={value => dispatch(preferenceActions.setTopN(value))}
      value={topN}
    />
  );
};

export const ConcentrationFilter: React.FC<ReactTable.FilterProps> = ({
  column: { filterValue, setFilter },
}) => {
  let left = 0,
    right = 1;
  if (filterValue) {
    left = filterValue[0];
    right = filterValue[1];
  }

  return (
    <div>
      <ConcentrationFilterWithoutSlider />
      <RangeSlider
        minValue={0}
        maxValue={1}
        leftValue={left}
        rightValue={right}
        percentage
        onChange={(left, right) => {
          onSliderChange(left, right, setFilter);
        }}
      />
    </div>
  );
};

export const VolumeFilter: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { avgVolInterval } = usePreferences();
  return (
    <FlatSelect
      options={['1m', '2m', '3m', '6m', '1y']}
      labels={['1m', '2m', '3m', '6m', '1y'].map(label => t(label))}
      onValueChange={value =>
        dispatch(preferenceActions.setAvgVolInterval(value))
      }
      value={avgVolInterval}
    />
  );
};

export const PriceRangeFilterWithoutSlider: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { priceRangeInterval } = usePreferences();

  return (
    <FlatSelect
      options={['5d', '1m', '3m', '6m', '52w']}
      labels={['5d', '1m', '3m', '6m', '52w'].map(label => t(label))}
      onValueChange={value =>
        dispatch(preferenceActions.setPriceRangeInterval(value))
      }
      value={priceRangeInterval}
    />
  );
};

export const PriceRangeFilter: React.FC<ReactTable.FilterProps> = ({
  column: { filterValue, setFilter },
}) => {
  const dispatch = useDispatch();
  const hasMembership = useMembership();

  let left = 0,
    right = 1;
  if (filterValue) {
    left = filterValue[0];
    right = filterValue[1];
  }

  return (
    <div>
      <PriceRangeFilterWithoutSlider />
      <RangeSlider
        minValue={0}
        maxValue={1}
        leftValue={left}
        rightValue={right}
        percentage
        // eslint-disable-next-line sonarjs/no-identical-functions
        onChange={(left, right) => {
          if (hasMembership) {
            onSliderChange(left, right, setFilter);
          } else {
            dispatch(
              uiActions.freeUserAlert.setFreeUserAlert(
                FreeUserAlertEnum.showPriceRangeFilterAlert,
                true
              )
            );
          }
        }}
      />
    </div>
  );
};

export const PriceChangeFilter: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { priceChangeInterval } = usePreferences();

  return (
    <FlatSelect<string>
      options={['1d', '3d', '5d', '1m', '1y']}
      labels={['1d', '3d', '5d', '1m', '1y'].map(label => t(label))}
      onValueChange={value =>
        dispatch(preferenceActions.setPriceChangeInterval(value))
      }
      value={priceChangeInterval}
    />
  );
};

export const ParticipantNumChangeFilterWithoutSlider: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { participantNumChangeInterval } = useSelector(
    state => state.preference
  );

  return (
    <FlatSelect<string>
      options={['5d', '1m', '3m', '52w']}
      labels={['5d', '1m', '3m', '52w'].map(label => t(label))}
      onValueChange={value =>
        dispatch(preferenceActions.setParticipantNumChangeInterval(value))
      }
      value={participantNumChangeInterval}
    />
  );
};

export const ParticipantNumChangeFilter: React.FC<ReactTable.FilterProps> = ({
  column: { filterValue, setFilter },
}) => {
  let left = 0,
    right = 1;
  if (filterValue) {
    left = filterValue[0];
    right = filterValue[1];
  }

  const dispatch = useDispatch();
  const hasMembership = useMembership();

  return (
    <div>
      <ParticipantNumChangeFilterWithoutSlider />
      <RangeSlider
        minValue={0}
        maxValue={1}
        leftValue={left}
        rightValue={right}
        percentage
        onChange={(left, right) => {
          if (hasMembership) {
            onSliderChange(left, right, setFilter);
          } else {
            dispatch(
              uiActions.freeUserAlert.setFreeUserAlert(
                FreeUserAlertEnum.showParticipantNumberChangeFilterAlert,
                true
              )
            );
          }
        }}
      />
    </div>
  );
};
